import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { getAuth } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import { toast } from 'sonner';
import EmissionsCard from '../../components/EmissionsCard';
import { computeEmissions } from '../../lib/emissions';
import { Document, Page } from 'react-pdf';
import * as pdfjs from 'pdfjs-dist';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import {
  ArrowLeft, Download, Edit, Save, X, Loader2, History, Lock, FileText, RefreshCw,
  Receipt, Gauge, List, Layers, Sparkles, ArrowRight, CheckCircle2, ZoomIn, ZoomOut, Building2, Leaf,
} from 'lucide-react';

const CHIVO = { fontFamily: 'Chivo, sans-serif' };
const BLUE = 'hsl(221,100%,33%)';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
const PDF_OPTIONS = {
  cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
  cMapPacked: true,
  standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
  wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`,
};

const fmtDate = (d) => {
  if (!d) return '—';
  try { const dt = new Date(d); return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
};
const fmtMoney = (v) => {
  if (v == null || v === '') return '—';
  const n = parseFloat(String(v).replace(/[$,\s]/g, ''));
  if (Number.isNaN(n)) return String(v);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
};

const STATEMENT_FIELDS = [
  ['vendor_name', 'Vendor'], ['account_no', 'Account No'], ['customer_name', 'Customer'],
  ['commodity', 'Commodity'], ['invoice_no', 'Invoice No'], ['billing_address', 'Billing Address'],
  ['city', 'City'], ['state', 'State'], ['postcode', 'Postcode'],
  ['issue_date', 'Issue Date'], ['due_date', 'Due Date'],
  ['period_from', 'Period From'], ['period_to', 'Period To'],
];
const FINANCIAL_FIELDS = [
  ['previous_balance', 'Previous Balance'], ['payments_received', 'Payments Received'],
  ['balance_forward', 'Balance Forward'], ['new_charges', 'New Charges'],
  ['gst', 'Tax / GST'], ['total_amount_due', 'Total Amount Due'],
];

const TABS = [
  { id: 'statement', label: 'Statement', icon: Receipt },
  { id: 'meters', label: 'Meter Reads', icon: Gauge },
  { id: 'usage', label: 'Usage Details', icon: List },
  { id: 'other', label: 'Other Charges', icon: Layers },
];

export default function TryBillView() {
  const { documentId } = useParams();
  const [params] = useSearchParams();
  const accountId = params.get('account_id');
  const navigate = useNavigate();
  const auth = getAuth();

  const [doc, setDoc] = useState(null);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [pdf, setPdf] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [editedFields, setEditedFields] = useState([]);
  const [activeTab, setActiveTab] = useState('statement');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(1.1);
  const [currentPage, setCurrentPage] = useState(1);
  const pdfContainerRef = useRef(null);
  const initedTab = useRef(false);

  useEffect(() => {
    if (!auth.token || !accountId) { navigate('/try'); return; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDoc = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data: res } = await api.get(`/try/document/${documentId}?account_id=${accountId}`);
      setDoc(res.document || {});
      setData(res.extracted_data || {});
      setEditedFields(res.user_edited_fields || []);
    } catch { toast.error('Could not load the parsed data.'); }
    finally { setLoading(false); }
  }, [documentId, accountId]);

  const fetchHistory = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/try/document/${documentId}/history?account_id=${accountId}`);
      setHistory(res.history || []);
    } catch { /* non-blocking */ }
  }, [documentId, accountId]);

  useEffect(() => { fetchDoc(); fetchHistory(); }, [fetchDoc, fetchHistory]);

  useEffect(() => {
    if (!initedTab.current && Array.isArray(data.accounts) && data.accounts.length) {
      setActiveTab('accounts');
      initedTab.current = true;
    }
  }, [data]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPdfLoading(true);
      try {
        const { data: res } = await api.get(`/try/document/${documentId}/file?account_id=${accountId}`);
        if (!cancelled) setPdf(res);
      } catch { if (!cancelled) setPdf({ error: true }); }
      finally { if (!cancelled) setPdfLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [documentId, accountId]);

  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  useEffect(() => {
    if (!pdf?.base64_data) { setPdfBlobUrl(null); return; }
    try {
      const bin = atob(pdf.base64_data);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([arr], { type: 'application/pdf' }));
      setPdfBlobUrl(url);
      return () => URL.revokeObjectURL(url);
    } catch { setPdfBlobUrl(null); }
  }, [pdf]);

  const pdfSrc = useMemo(() => {
    if (pdfBlobUrl) return pdfBlobUrl;
    if (pdf?.pdf_url) return pdf.pdf_url;
    return null;
  }, [pdfBlobUrl, pdf]);

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // Scroll the PDF pane to a specific page (mirrors the Intake Hub viewer).
  const navigateToPage = useCallback((pageNum) => {
    if (!pageNum || !numPages) return;
    const page = parseInt(pageNum, 10);
    if (Number.isNaN(page) || page < 1 || page > numPages) return;
    setCurrentPage(page);
    const el = pdfContainerRef.current?.querySelector(`[data-page-number="${page}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [numPages]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/try/document/${documentId}?account_id=${accountId}`, { extracted_data: data });
      toast.success('Changes saved.', { duration: 2000, position: 'bottom-right' });
      setEditMode(false);
      fetchDoc(); fetchHistory();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save.'); }
    finally { setSaving(false); }
  };

  // Re-parse once: re-runs extraction while preserving every field you already
  // filled/corrected. Allowed a single time per bill.
  const reparse = async () => {
    if ((doc?.reparse_count || 0) >= 1) return;
    setReparsing(true);
    try {
      await api.post(`/accounts/${accountId}/documents/${documentId}/reprocess`, { mode: 'all' });
      toast.info('Re-parsing this bill… your manual edits are preserved.', { position: 'bottom-right' });
      const started = Date.now();
      const iv = setInterval(async () => {
        try {
          const { data: res } = await api.get(`/try/document/${documentId}?account_id=${accountId}`);
          const st = res.document?.status;
          if (st === 'completed') {
            clearInterval(iv); setReparsing(false);
            setDoc(res.document || {}); setData(res.extracted_data || {}); setEditedFields(res.user_edited_fields || []); fetchHistory();
            toast.success('Re-parsing complete.');
          } else if (st === 'failed') {
            clearInterval(iv); setReparsing(false);
            toast.error('Re-parsing failed.');
          }
        } catch { /* keep polling */ }
        if (Date.now() - started > 120000) { clearInterval(iv); setReparsing(false); }
      }, 3000);
    } catch (e) {
      setReparsing(false);
      toast.error(e.response?.data?.detail || 'Could not start re-parsing.');
    }
  };

  const downloadPdf = () => {
    if (!pdfSrc) return;
    const a = document.createElement('a');
    a.href = pdfSrc;
    a.download = pdf?.filename || 'bill.pdf';
    document.body.appendChild(a); a.click(); a.remove();
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${(doc?.filename || 'bill').replace(/\.pdf$/i, '')}.json`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success('JSON downloaded.');
  };

  const meterReads = Array.isArray(data.meter_reads) ? data.meter_reads : [];
  const usageDetails = Array.isArray(data.usage_details) ? data.usage_details : [];
  const otherCharges = Array.isArray(data.other_charges) ? data.other_charges : [];
  const accounts = Array.isArray(data.child_invoices) ? data.child_invoices
    : (Array.isArray(data.accounts) ? data.accounts : []);
  const distinctAccounts = new Set(accounts.map((a) => a.child_account_no || a.account_number).filter(Boolean)).size;
  const isConsolidated = doc?.bill_type === 'consolidated' || accounts.length > 0;
  const tabs = isConsolidated ? [{ id: 'accounts', label: 'Accounts', icon: Building2 }, ...TABS] : TABS;

  if (loading) {
    return <div className="h-screen flex items-center justify-center bg-white"><Loader2 className="h-8 w-8 animate-spin" style={{ color: BLUE }} /></div>;
  }

  return (
    <div className="h-screen flex flex-col bg-white">
      <TooltipProvider delayDuration={150}>
        {/* Header */}
        <header className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" className="rounded-none" onClick={() => navigate('/try')} data-testid="trybill-back">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <img src="/billportal-logo.png" alt="" className="h-7 w-7" />
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 text-slate-400 shrink-0" />
              <span className="font-mono text-sm font-medium truncate" data-testid="trybill-filename">{doc?.filename}</span>
              {isConsolidated && <span className="px-2 py-0.5 text-[10px] uppercase tracking-wide font-bold text-white shrink-0" style={{ background: BLUE }}>Consolidated</span>}
              {isConsolidated && accounts.length > 0 && (
                <span className="text-[11px] text-slate-500 shrink-0" data-testid="trybill-account-count">
                  {distinctAccounts} account{distinctAccounts !== 1 ? 's' : ''} · {accounts.length} line item{accounts.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-none" onClick={() => { fetchHistory(); setShowHistory(true); }} data-testid="trybill-history-btn">
              <History className="h-4 w-4 mr-1.5" /> History
            </Button>
            {pdfSrc && (
              <Button variant="outline" size="sm" className="rounded-none" onClick={downloadPdf} data-testid="trybill-download-pdf">
                <Download className="h-4 w-4 mr-1.5" /> PDF
              </Button>
            )}
            <Button variant="outline" size="sm" className="rounded-none" onClick={downloadJson} data-testid="trybill-download-json">
              <Download className="h-4 w-4 mr-1.5" /> JSON
            </Button>
            {editMode && (
              <Button variant="outline" size="sm" className="rounded-none" onClick={() => { setEditMode(false); fetchDoc(); }} data-testid="trybill-cancel">
                <X className="h-4 w-4 mr-1.5" /> Cancel
              </Button>
            )}
            {!editMode && (
              <Button variant="outline" size="sm" className="rounded-none"
                onClick={reparse}
                disabled={reparsing || (doc?.reparse_count || 0) >= 1 || doc?.status === 'processing'}
                title={(doc?.reparse_count || 0) >= 1 ? 'This bill has already been re-parsed once. Your edits are preserved — edit any field directly.' : 'Re-parse once to fill any gaps — your edits are kept.'}
                data-testid="trybill-reparse">
                {reparsing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                {reparsing ? 'Re-parsing…' : (doc?.reparse_count || 0) >= 1 ? 'Re-parsed' : 'Re-parse'}
              </Button>
            )}
            <Button size="sm" className="rounded-none font-bold uppercase" style={{ background: BLUE }}
              onClick={() => (editMode ? save() : setEditMode(true))} disabled={saving} data-testid="trybill-edit-save">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : editMode ? <Save className="h-4 w-4 mr-1.5" /> : <Edit className="h-4 w-4 mr-1.5" />}
              {saving ? 'Saving…' : editMode ? 'Save' : 'Edit'}
            </Button>
          </div>
        </header>

        {/* Frozen workflow bar — work allocation is a registered feature */}
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-2 flex items-center justify-between" data-testid="trybill-workflow-bar">
          <div className="flex items-center gap-5">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 cursor-not-allowed opacity-70" data-testid="trybill-status-frozen">
                  <Label className="text-[11px] uppercase font-semibold text-slate-500">Status</Label>
                  <span className="inline-flex items-center gap-1.5 border border-slate-300 bg-white px-3 py-1 text-xs text-slate-500">
                    <Lock className="h-3 w-3" /> Review
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs"><p className="text-xs">Review &amp; Approve statuses are available once you register — mark bills reviewed and lock approved ones.</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 cursor-not-allowed opacity-70" data-testid="trybill-assign-frozen">
                  <Label className="text-[11px] uppercase font-semibold text-slate-500">Assigned</Label>
                  <span className="inline-flex items-center gap-1.5 border border-slate-300 bg-white px-3 py-1 text-xs text-slate-500">
                    <Lock className="h-3 w-3" /> Unassigned
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs"><p className="text-xs">Assign bills to teammates to claim them — so high-volume ops teams never review the same bill twice. Available after you register.</p></TooltipContent>
            </Tooltip>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 text-white" style={{ background: BLUE }}>
            <Sparkles className="h-3 w-3" /> Work allocation unlocks on register
          </span>
        </div>

        {/* Split view */}
        <div className="flex-1 grid grid-cols-2 grid-rows-1 min-h-0">
          {/* PDF */}
          <div className="border-r border-slate-200 bg-slate-100 min-h-0 flex flex-col" data-testid="trybill-pdf">
            <div className="flex items-center gap-2 px-3 py-1.5 border-b border-slate-200 bg-white shrink-0">
              <Button variant="outline" size="sm" className="rounded-none h-7 w-7 p-0" onClick={() => setScale((s) => Math.max(0.6, +(s - 0.15).toFixed(2)))} data-testid="trybill-zoom-out"><ZoomOut className="h-3.5 w-3.5" /></Button>
              <span className="text-xs font-mono w-12 text-center">{Math.round(scale * 100)}%</span>
              <Button variant="outline" size="sm" className="rounded-none h-7 w-7 p-0" onClick={() => setScale((s) => Math.min(2.2, +(s + 0.15).toFixed(2)))} data-testid="trybill-zoom-in"><ZoomIn className="h-3.5 w-3.5" /></Button>
              {numPages ? <span className="text-xs text-slate-500 ml-2" data-testid="trybill-page-indicator">Page {currentPage} of {numPages}</span> : null}
            </div>
            <div className="flex-1 overflow-auto p-3" ref={pdfContainerRef}>
              {pdfLoading ? (
                <div className="h-full flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" style={{ color: BLUE }} /></div>
              ) : pdfSrc ? (
                <Document file={pdfSrc} options={PDF_OPTIONS} onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                  loading={<div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" style={{ color: BLUE }} /></div>}
                  error={<div className="py-16 text-center text-sm text-slate-400">Couldn't render the PDF preview — use “PDF” above to download it.</div>}>
                  {Array.from(new Array(numPages || 0), (el, i) => (
                    <Page key={i} pageNumber={i + 1} width={Math.round(560 * scale)} renderTextLayer={false} renderAnnotationLayer={false}
                      data-page-number={i + 1}
                      className={`mb-3 shadow-sm mx-auto bg-white transition-all ${currentPage === i + 1 ? 'ring-4 ring-blue-500 ring-offset-2' : ''}`} />
                  ))}
                </Document>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-slate-400">
                  <FileText className="h-12 w-12 mb-3 opacity-40" />
                  <p className="text-sm">PDF preview not available</p>
                  <p className="text-xs mt-1">The parsed data is on the right.</p>
                </div>
              )}
            </div>
          </div>

          {/* Data */}
          <div className="min-h-0 overflow-auto">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full grid rounded-none border-b sticky top-0 z-10 bg-white" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0,1fr))` }}>
                {tabs.map(({ id, label, icon: Icon }) => (
                  <TabsTrigger key={id} value={id} className="rounded-none text-xs" data-testid={`trybill-tab-${id}`}>
                    <Icon className="h-3.5 w-3.5 mr-1" />{label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* ACCOUNTS (consolidated) */}
              {isConsolidated && (
                <TabsContent value="accounts" className="p-5 space-y-4 mt-0" data-testid="trybill-accounts">
                  <ConsolidatedAccounts accounts={accounts} data={data} meterReads={meterReads} usageDetails={usageDetails} onNavigatePage={navigateToPage} />
                </TabsContent>
              )}

              {/* STATEMENT */}
              <TabsContent value="statement" className="p-5 space-y-5 mt-0" data-testid="trybill-statement">
                <EmissionsCard parsed={{ commodity: data.commodity, usage_details: usageDetails, meter_reads: meterReads, accounts: data.accounts, primary_service_state: data.state, postcode: data.postcode, country: data.country, issue_date: data.issue_date }} />

                <div>
                  <h3 className="text-sm font-bold mb-2" style={CHIVO}>Bill details</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {STATEMENT_FIELDS.map(([k, label]) => (
                      <div key={k}>
                        <Label className="text-[10px] uppercase tracking-wide font-medium text-slate-500 flex items-center gap-1">
                          {label}
                          {editedFields.includes(k) && <span className="inline-flex items-center px-1 text-[8px] font-bold uppercase bg-blue-50 text-blue-700 border border-blue-200" title="You edited this — a re-parse keeps your value" data-testid={`trybill-edited-${k}`}>Edited</span>}
                        </Label>
                        <Input className="rounded-none mt-1 font-mono text-xs h-8" value={data[k] ?? ''} disabled={!editMode}
                          onChange={(e) => setField(k, e.target.value)} data-testid={`trybill-field-${k}`} />
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-bold mb-2" style={CHIVO}>Financial summary</h3>
                  <Table>
                    <TableBody>
                      {FINANCIAL_FIELDS.map(([k, label]) => (
                        <TableRow key={k} className={k === 'total_amount_due' ? 'bg-slate-50 font-semibold' : ''}>
                          <TableCell className="text-xs py-1.5">{label} {editedFields.includes(k) && <span className="inline-flex items-center px-1 text-[8px] font-bold uppercase bg-blue-50 text-blue-700 border border-blue-200" title="You edited this — a re-parse keeps your value" data-testid={`trybill-edited-${k}`}>Edited</span>}</TableCell>
                          <TableCell className="text-xs py-1.5 text-right">
                            {editMode ? (
                              <Input className="rounded-none h-7 text-xs font-mono text-right w-32 ml-auto" value={data[k] ?? ''} onChange={(e) => setField(k, e.target.value)} data-testid={`trybill-fin-${k}`} />
                            ) : (
                              <span className="font-mono">{fmtMoney(data[k])}</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>

              {/* METERS */}
              <TabsContent value="meters" className="p-5 mt-0" data-testid="trybill-meters">
                {meterReads.length ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-[10px] uppercase w-12">Pg</TableHead>
                        <TableHead className="text-[10px] uppercase">Meter</TableHead>
                        <TableHead className="text-[10px] uppercase">Commodity</TableHead>
                        <TableHead className="text-[10px] uppercase text-right">Previous</TableHead>
                        <TableHead className="text-[10px] uppercase text-right">Current</TableHead>
                        <TableHead className="text-[10px] uppercase text-right">Usage</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {meterReads.map((m, i) => (
                        <TableRow key={i} data-testid={`trybill-meter-${i}`}
                          className={m.page_number ? 'cursor-pointer hover:bg-slate-50' : ''}
                          onClick={() => m.page_number && navigateToPage(m.page_number)}
                          title={m.page_number ? `Click to go to page ${m.page_number}` : ''}>
                          <PageCell page={m.page_number} onNavigate={navigateToPage} />
                          <TableCell className="text-xs font-mono">{m.meter_number || '—'}</TableCell>
                          <TableCell className="text-xs">{m.commodity || data.commodity || '—'}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{m.previous_read ?? '—'}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{m.current_read ?? '—'}</TableCell>
                          <TableCell className="text-xs text-right font-mono font-medium">{m.usage ?? (m.current_read && m.previous_read ? Number(m.current_read) - Number(m.previous_read) : '—')} {m.uom || m.unit || ''}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : <Empty text="No meter reads on this bill." />}
              </TabsContent>

              {/* USAGE */}
              <TabsContent value="usage" className="p-5 mt-0" data-testid="trybill-usage">
                {usageDetails.length ? (
                  <>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-[10px] uppercase w-12">Pg</TableHead>
                        <TableHead className="text-[10px] uppercase">Acct</TableHead>
                        <TableHead className="text-[10px] uppercase">Meter</TableHead>
                        <TableHead className="text-[10px] uppercase">Description</TableHead>
                        <TableHead className="text-[10px] uppercase text-right">Usage</TableHead>
                        <TableHead className="text-[10px] uppercase text-right">Rate</TableHead>
                        <TableHead className="text-[10px] uppercase text-right">Charge</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {usageDetails.map((u, i) => (
                        <TableRow key={i} data-testid={`trybill-usage-${i}`}
                          className={u.page_number ? 'cursor-pointer hover:bg-slate-50' : ''}
                          onClick={() => u.page_number && navigateToPage(u.page_number)}
                          title={u.page_number ? `Click to go to page ${u.page_number}` : ''}>
                          <PageCell page={u.page_number} onNavigate={navigateToPage} />
                          <TableCell className="text-xs font-mono">{u.child_account_no || '—'}</TableCell>
                          <TableCell className="text-xs font-mono">{u.meter_number || '—'}</TableCell>
                          <TableCell className="text-xs">{u.line_item_description || u.description || '—'}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{(u.usage ?? u.usage_quantity ?? u.quantity) != null ? `${u.usage ?? u.usage_quantity ?? u.quantity} ${u.uom || u.unit || ''}` : '—'}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{u.usage_rate ?? u.rate ?? '—'}</TableCell>
                          <TableCell className="text-xs text-right font-mono font-medium">{fmtMoney(u.charges ?? u.charge ?? u.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </>
                ) : <Empty text="No usage line items on this bill." />}
              </TabsContent>

              {/* OTHER */}
              <TabsContent value="other" className="p-5 mt-0" data-testid="trybill-other">
                {otherCharges.length ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-[10px] uppercase w-12">Pg</TableHead>
                        <TableHead className="text-[10px] uppercase">Acct</TableHead>
                        <TableHead className="text-[10px] uppercase">Description</TableHead>
                        <TableHead className="text-[10px] uppercase text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {otherCharges.map((o, i) => (
                        <TableRow key={i} data-testid={`trybill-other-${i}`}
                          className={o.page_number ? 'cursor-pointer hover:bg-slate-50' : ''}
                          onClick={() => o.page_number && navigateToPage(o.page_number)}
                          title={o.page_number ? `Click to go to page ${o.page_number}` : ''}>
                          <PageCell page={o.page_number} onNavigate={navigateToPage} />
                          <TableCell className="text-xs font-mono">{o.child_account_no || '—'}</TableCell>
                          <TableCell className="text-xs">{o.line_item_description || o.description || '—'}</TableCell>
                          <TableCell className="text-xs text-right font-mono font-medium">{fmtMoney(o.charge ?? o.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : <Empty text="No other charges on this bill." />}
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Upsell footer */}
        <div className="border-t border-slate-200 bg-slate-50 px-5 py-3 flex items-center justify-between gap-4" data-testid="trybill-upsell">
          <p className="text-xs text-slate-600">
            <b>Register</b> to experience more — tracking missing bills, following up with suppliers, setting up accounts,
            payments &amp; generating payment files, and more enterprise capabilities.
            <span className="ml-1 text-slate-400">Free forever · 5 bills / accounts per month.</span>
          </p>
          <Button className="rounded-none font-bold uppercase tracking-wide shrink-0" style={{ background: BLUE }}
            onClick={() => navigate('/try?step=register')} data-testid="trybill-register-cta">
            Register free <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      </TooltipProvider>

      {/* History dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-lg rounded-none" data-testid="trybill-history-dialog">
          <DialogHeader><DialogTitle style={CHIVO} className="tracking-tight flex items-center gap-2"><History className="h-4 w-4" /> Change history</DialogTitle></DialogHeader>
          {history.length ? (
            <div className="space-y-2 max-h-96 overflow-auto">
              {history.map((h, i) => (
                <div key={i} className="flex items-start gap-2 border border-slate-200 p-2.5 text-xs" data-testid={`trybill-history-${i}`}>
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-slate-800">{h.user_name || 'You'} · {h.action || 'edited'}</div>
                    {Array.isArray(h.changed_fields) && h.changed_fields.length > 0 && (
                      <div className="text-slate-500 truncate">Fields: {h.changed_fields.join(', ')}</div>
                    )}
                    <div className="text-slate-400">{fmtDate(h.created_at)} · {h.created_at ? new Date(h.created_at).toLocaleTimeString() : ''}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 py-6 text-center">No edits yet — your changes will be logged here with who &amp; when.</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Empty = ({ text }) => (
  <div className="py-12 text-center text-sm text-slate-400">{text}</div>
);

const StatBox = ({ label, value }) => (
  <div className="border border-slate-200 bg-slate-50 p-3">
    <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">{label}</div>
    <div className="text-xl font-black tracking-tight text-slate-900">{value}</div>
  </div>
);

const kg = (v) => (v >= 1000 ? `${(v / 1000).toLocaleString('en-US', { maximumFractionDigits: 2 })} t` : `${Math.round(v).toLocaleString()} kg`);

const PageCell = ({ page, onNavigate }) => (
  <TableCell className="text-center py-1.5">
    {page ? (
      <Badge variant="secondary" className="rounded-none text-[10px] px-1.5 py-0.5 cursor-pointer hover:bg-blue-100"
        onClick={(e) => { e.stopPropagation(); onNavigate?.(page); }}
        data-testid={`trybill-pagebadge-${page}`}>
        p.{page}
      </Badge>
    ) : <span className="text-[11px] text-slate-300">—</span>}
  </TableCell>
);

const ConsolidatedAccounts = ({ accounts, data, meterReads = [], usageDetails = [], onNavigatePage }) => {
  // child_invoices omit usage, so derive per-account usage from meter reads
  // (preferred) or usage lines, keyed by child_account_no.
  const usageByChild = useMemo(() => {
    const push = (dst, acct, commodity, qty, uom) => {
      const q = Number(qty) || 0;
      if (!acct || q <= 0) return;
      dst[acct] = dst[acct] || {};
      const key = (commodity || 'x').toLowerCase();
      if (!dst[acct][key]) dst[acct][key] = { commodity, quantity: 0, uom };
      dst[acct][key].quantity += q;
    };
    const meterMap = {};
    for (const m of meterReads) push(meterMap, m.child_account_no, m.commodity, m.calculated_usage ?? m.raw_usage ?? m.usage, m.uom || m.unit);
    const usageMap = {};
    for (const u of usageDetails) push(usageMap, u.child_account_no, u.commodity, u.usage_quantity ?? u.usage ?? u.quantity, u.uom || u.unit);
    const out = {};
    for (const acct of new Set([...Object.keys(meterMap), ...Object.keys(usageMap)])) {
      out[acct] = Object.values(meterMap[acct] || usageMap[acct] || {});
    }
    return out;
  }, [meterReads, usageDetails]);

  const rows = accounts.map((a) => {
    const acctNo = a.child_account_no || a.account_number;
    let lines = usageByChild[acctNo] || [];
    if (!lines.length && (a.usage?.quantity ?? a.usage_quantity) != null) {
      lines = [{ commodity: a.commodity, quantity: a.usage?.quantity ?? a.usage_quantity, uom: a.usage?.unit ?? a.usage_unit }];
    }
    const em = computeEmissions(lines);
    const addr = a.unit_address || (typeof a.service_address === 'string' ? a.service_address : a.service_address?.street) || '—';
    return { a, acctNo, addr, lines, kg: em.total_kg };
  });
  const distinctAccts = new Set(accounts.map((a) => a.child_account_no || a.account_number).filter(Boolean)).size;
  const totalCharges = accounts.reduce((s, a) => s + (parseFloat(String(a.new_charges ?? 0).replace(/[$,\s]/g, '')) || 0), 0);
  const totalKg = rows.reduce((s, r) => s + r.kg, 0);
  return (
    <>
      <EmissionsCard parsed={{ commodity: data.commodity, usage_details: usageDetails, meter_reads: meterReads, accounts, primary_service_state: data.state, postcode: data.postcode, country: data.country, issue_date: data.issue_date }} />
      <div className="grid grid-cols-4 gap-3">
        <StatBox label="Accounts" value={distinctAccts} />
        <StatBox label="Line items" value={accounts.length} />
        <StatBox label="Statement total" value={fmtMoney(totalCharges)} />
        <StatBox label="Total CO₂e" value={kg(totalKg)} />
      </div>
      <div className="border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-[10px] uppercase w-12">Pg</TableHead>
              <TableHead className="text-[10px] uppercase">Account</TableHead>
              <TableHead className="text-[10px] uppercase">Service Address</TableHead>
              <TableHead className="text-[10px] uppercase">Commodity</TableHead>
              <TableHead className="text-[10px] uppercase text-right">Usage</TableHead>
              <TableHead className="text-[10px] uppercase text-right">Charges</TableHead>
              <TableHead className="text-[10px] uppercase text-right">CO₂e</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(({ a, acctNo, addr, lines, kg: rkg }, i) => (
              <TableRow key={i} data-testid={`trybill-acct-${i}`}
                className={a.page_number ? 'cursor-pointer hover:bg-slate-50' : ''}
                onClick={() => a.page_number && onNavigatePage?.(a.page_number)}
                title={a.page_number ? `Click to go to page ${a.page_number}` : ''}>
                <PageCell page={a.page_number} onNavigate={onNavigatePage} />
                <TableCell className="text-xs font-mono">{acctNo || '—'}</TableCell>
                <TableCell className="text-xs">{addr}</TableCell>
                <TableCell className="text-xs">{a.commodity || '—'}</TableCell>
                <TableCell className="text-xs text-right font-mono">
                  {lines.length ? lines.map((l) => `${Number(l.quantity).toLocaleString()} ${l.uom || ''}`.trim()).join(', ') : '—'}
                </TableCell>
                <TableCell className="text-xs text-right font-mono">{fmtMoney(a.new_charges)}</TableCell>
                <TableCell className="text-xs text-right font-mono font-medium text-emerald-700">
                  {rkg > 0 ? <span className="inline-flex items-center gap-1"><Leaf className="h-3 w-3" />{kg(rkg)}</span> : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
};
